/**
 * MetricCard -- Displays a key performance indicator with optional delta.
 * @layer atom
 * @package @plerity/ui-core
 */

import { memo } from "react";
import { DemoBadge, useDemoMode } from "../DemoMode/DemoMode";
import { cn } from "../utils/cn";
import type { DeltaDirection, MetricCardProps, MetricFormat } from "./MetricCard.types";
import { cardVariants } from "./MetricCard.variants";

// Re-exported so `import { cardVariants } from "@plerity/ui-core"` (and the
// component barrel `./MetricCard`) keep working after the leaf-module extraction.
export { cardVariants };

function formatValue(value: number | string, format: MetricFormat, prefix?: string, suffix?: string): string {
  let formatted: string;

  if (typeof value === "string") {
    formatted = value;
  } else if (format === "currency") {
    formatted = `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  } else if (format === "percentage") {
    formatted = `${value.toFixed(1)}%`;
  } else {
    formatted = value.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }

  if (prefix) formatted = `${prefix}${formatted}`;
  if (suffix) formatted = `${formatted}${suffix}`;
  return formatted;
}

function formatDelta(delta: number, format: MetricFormat): string {
  const sign = delta >= 0 ? "+" : "";
  if (format === "percentage") return `${sign}${delta.toFixed(1)}%`;
  if (format === "currency") return `${sign}$${Math.abs(delta).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
  return `${sign}${delta.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function getDeltaColor(direction: DeltaDirection): string {
  // green/red are static (theme-invariant) scales; their dark: shift to a
  // lighter shade is correct.
  //
  // The neutral case NEEDS its `dark:` pair. The "auto-inverting gray ramp"
  // this previously relied on is MOBILE-ONLY: on web, `inject-runtime-tokens`
  // writes the gray scale into each app's `index.css` as PRE-RESOLVED literals
  // that are identical in `:root` and `html.dark` (see the generated header in
  // e.g. `apps/ai/src/index.css`). So a bare `text-gray-500` stays #64748b in
  // dark mode = 3.07:1 on the dark card (#1e293b) — a WCAG 1.4.3 FAIL.
  // `dark:text-gray-400` (#94a3b8) measures 5.71:1 and is not a double-flip.
  if (direction === "increase") return "text-success-strong";
  if (direction === "decrease") return "text-danger-strong";
  return "text-gray-500 dark:text-gray-400";
}

export const MetricCard = memo(function MetricCard({
  label,
  value,
  delta,
  deltaDirection = "neutral",
  format = "number",
  prefix,
  suffix,
  icon,
  variant,
  className,
  loading = false,
}: MetricCardProps) {
  const isDemo = useDemoMode();
  if (loading) {
    return (
      <output className={cn(cardVariants({ variant }), "animate-pulse block", className)} aria-label="Loading metric">
        <div className="h-4 w-24 rounded bg-gray-100 dark:bg-gray-700" />
        <div className="mt-2 h-8 w-32 rounded bg-gray-100 dark:bg-gray-700" />
      </output>
    );
  }

  const formattedValue = formatValue(value, format, prefix, suffix);

  return (
    <section className={cn(cardVariants({ variant }), className)} aria-label={label}>
      {/* `min-w-0` on the label row + `truncate` on the label span prevents
          long labels from overflowing into the icon slot. */}
      <div className="flex items-center justify-between gap-2 min-w-0">
        {/* `dark:text-gray-400` is REQUIRED — see getDeltaColor() above: the
            web gray ramp is static, so a bare `text-gray-500` label is 3.07:1
            on the dark card. This label is on every KPI row in the fleet. */}
        <span className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate min-w-0" title={label}>
          {label}
        </span>
        <span className="flex shrink-0 items-center gap-1.5">
          {isDemo && <DemoBadge />}
          {icon && (
            <span className="text-gray-400" aria-hidden="true">
              {icon}
            </span>
          )}
        </span>
      </div>
      {/* The VALUE wraps (`break-words`) instead of truncating: it is the one
          piece of the card the reader came for, and in a 2-column KPI grid at
          375px a date like "10/10/2026" was ellipsized to "10/10/20…" (2026-10-11
          AI visual QA). `text-xl` below `sm` keeps typical values on one line;
          `min-w-0` lets the card shrink so the wrap engages inside grid tracks. */}
      <div className="mt-1 min-w-0">
        <span
          className="block break-words text-xl font-semibold text-gray-900 sm:text-2xl dark:text-gray-100"
          title={formattedValue}
        >
          {formattedValue}
        </span>
      </div>
      {delta != null && (
        <div className="mt-1 min-w-0">
          <span
            className={cn("block truncate text-sm font-medium", getDeltaColor(deltaDirection))}
            title={formatDelta(delta, format)}
          >
            {formatDelta(delta, format)}
          </span>
        </div>
      )}
    </section>
  );
});

MetricCard.displayName = "MetricCard";
